// A type import: the custom frontends import this file, and must not pull in SearchForm
import type { SearchFormParams } from './SearchForm';
import { safeLocalStorage } from '../../utils/storage';

const STORAGE_VERSION = 'v3';
const STORAGE_KEY = 'userSearchFormParams';

function getVersionedKey(): string {
  return `${STORAGE_KEY}_${STORAGE_VERSION}`;
}

export function saveSearchParams(params: SearchFormParams): void {
  // Save params to localStorage, omitting username
  const { username, ...paramsToStore } = params;
  try {
    safeLocalStorage.setItem(getVersionedKey(), JSON.stringify(paramsToStore));
  } catch {
    // Silently fail if localStorage is not available
  }
}

export function loadInitialSearchParams(): Partial<SearchFormParams> {
  try {
    const raw = safeLocalStorage.getItem(getVersionedKey());
    if (raw) {
      const params = JSON.parse(raw);
      // username should never be restored
      delete params.username;
      return params;
    }
  } catch {
    // Silently fail if localStorage is not available or data is corrupted
  }
  return {};
}

export function clearSearchParams(): void {
  try {
    safeLocalStorage.removeItem(getVersionedKey());
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Saves `age` as the search's age range unless the visitor already has one,
 * merged into whatever else is saved (`loadInitialSearchParams` takes partial
 * params). The custom frontends' mature pages call it, so a guest who signs
 * up from one lands on `/search` among older profiles. Returns whether it saved.
 */
export function presetSearchAge(age: [number, number]): boolean {
  try {
    const raw = safeLocalStorage.getItem(getVersionedKey());
    const saved: unknown = raw ? JSON.parse(raw) : {};
    const params = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved as Record<string, unknown> : {};
    if (Array.isArray(params['age'])) {
      return false;
    }
    safeLocalStorage.setItem(getVersionedKey(), JSON.stringify({ ...params, age }));
    return true;
  } catch {
    // Corrupted data is left to loadInitialSearchParams, which ignores it
    return false;
  }
}
